// ---------- interface & interactions ----------

import { alphabet, codeOrder } from './alphabet';
import { currentUser } from './account';
import { notePlay } from './adPacing';
import { prepareBreakAd, prepareInterstitial } from './ads';
import { alphabetLabel, currentLang, t } from './i18n';
import {
  anim,
  noMotion,
  animBurst,
  animFinal,
  animFlare,
  animFlareEnd,
  animFlyTo,
  animNow,
  animPoke,
  animPop,
  animReplay,
  animResetGrid,
  animReplaySkip,
  animRing,
  animShake,
  animTwinkles,
  animWake,
  FINAL_MS,
  flareLit,
} from './anim';
import { pop } from './audio';
import { fetchFinishers, isCreation } from './api';
import { openedTodaysDaily } from './daily';
import { buzz, canShareFile, shareSheet } from './platform';
import { closeShareSheet, openShareSheet, wireShareSheet } from './sharePanel';
import { coachNouvelleFeuille } from './coach';
import { ordreNouvelleFeuille, ordrePastilleAVenir } from './ordre';
import { closeGestureDialog } from './gestureDialog';
import { closePieceDone } from './mascot';
import { closeLangSheet, closeRecoveryDialog, closeUsernameDialog } from './prefs';
import { rateProgress, saveProgress } from './progress';
import { noteShare } from './shares';
import { markGameFinished, markSheetRated } from './review';
import { settings, wireSettings } from './settings';
import {
  applyZoom,
  flyToGuide,
  flyToWhole,
  flyToZone,
  guideHit,
  handBackView,
  baseNeedsRebuild,
  invalidateBase,
  invalidateHalo,
  invalidateLayers,
  settleGesture,
  render,
  renderPending,
  renderSoon,
  renderThumb,
  resetPlay,
  revealTaps,
  updateProgress,
  zonesOnScreen,
} from './render';
import type { CodeCount, Progress } from './render';
import { S, ZOOM_MAX, ZOOM_MIN, measureScene } from './state';

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => {
  const e = document.getElementById(id);
  if (!e) throw new Error(`element not found: ${id}`);
  return e as T;
};

// ---------- fills ----------
/**
 * The fill itself, with no feedback whatsoever. Split out because a tap and a swept stroke have
 * to fill IDENTICALLY — this is what the saved taps replay — while celebrating very differently.
 *
 * One touch, ONE cell — which is the floor and not what ships: `flood` is on by default and no
 * switch turns it off any more, so in practice every fill carries on through its zone via
 * floodFrom(). The sweep is not left without a job by that: it chains ZONES, where it used to
 * chain cells.
 */
function fillCell(r: number, c: number): boolean {
  if (!S.target[r][c] || S.filled[r][c]) return false;
  S.filled[r][c] = true;
  S.taps.push(r * S.gridW + c);

  const t = animNow();
  // Le temps de jeu de la pub de pause (cf. src/adPacing.ts). Au franchissement du seuil, on
  // précharge TOUT DE SUITE, pendant que le joueur colorie encore : quand il quittera la feuille,
  // la pub sera prête, ou il partira sans — jamais on ne le fera attendre à la porte.
  if (notePlay(t)) void prepareBreakAd();
  // Combo: successes in quick succession. Raises the sound pitch by a semitone per step
  // and grows the label — that's what makes chaining cells feel rewarding.
  S.combo = t - S.comboAt < 2600 ? S.combo + 1 : 1;
  S.comboAt = t;
  S.score++;
  return true;
}

/** Four-connected, and not eight: on a dithered grid the cells of one symbol touch corners all
 *  over the sheet, so diagonals would let a single tap leak into zones the eye reads as separate
 *  — half the drawing for one touch. */
const NEIGHBOURS: ReadonlyArray<readonly [number, number]> = [
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
];

/**
 * The `flood` option: carries the fill on through the cells of symbol `m` contiguous to (r0, c0),
 * which fillCell() has ALREADY filled. Returns every cell filled, seed first, for the animation
 * to bloom from — animPop switches to its wave above a few hundred, so a zone of any size costs
 * the same.
 *
 * Cells already filled are walls, not passages: a zone cut in two by earlier work stays two zones,
 * which is the only reading that matches what is on the paper. And since fillCell() marks the cell
 * as it takes it, `filled` doubles as the visited set — no second bitmap to keep in step.
 */
function floodFrom(r0: number, c0: number, m: number): Array<[number, number]> {
  const zone: Array<[number, number]> = [[r0, c0]];
  // Breadth-first over the array itself: the tail is the queue, and the cells come out in rings
  // around the seed — the order animPop's delay is computed from anyway.
  for (let i = 0; i < zone.length; i++) {
    const [r, c] = zone[i];
    for (const [dr, dc] of NEIGHBOURS) {
      const rr = r + dr;
      const cc = c + dc;
      if (rr < 0 || cc < 0 || rr >= S.gridH || cc >= S.gridW) continue;
      if (S.target[rr][cc] !== m) continue;
      if (!fillCell(rr, cc)) continue;
      zone.push([rr, cc]);
    }
  }
  return zone;
}

/**
 * Cell the fill being written back ended on, for the length of its updateProgress() call — read
 * by onCodeProgress(), which is where a symbol running out of cells is noticed. A milestone is
 * owed to a fill and to nothing else: restoring a save also completes symbols, and celebrating
 * those on load would be congratulating the player for another session's work.
 */
let filling: [number, number] | null = null;

/** Everything a fill writes back. Once per tap — and once per FRAME of a stroke rather than once
 *  per cell, since a sweep can fill a dozen of them between two pointer events.
 *
 *  `soon` : le balayage DEMANDE son image (renderSoon) au lieu de la dessiner. Chaque case peinte
 *  éclôt, donc la boucle d'animation tourne pendant tout le balayage et dessine déjà chaque image ;
 *  le rendu immédiat en faisait deux par image (5 images doublées sur un balayage de 30 pas en
 *  peinture, 1 ensuite). Sauf pour la case qui termine la feuille : finishGame() part juste après, et
 *  elle doit trouver la feuille dessinée comme avant. */
function afterFill(r: number, c: number, soon = false): void {
  S.hover = null;
  S.hoverKey = '';
  invalidateHalo();
  // Seulement si la couche montre les numéros (cf. baseNeedsRebuild) : sinon les cases remplies y
  // sont vides avant comme après, et la reconstruire coûtait une image entière au moment du tap.
  if (baseNeedsRebuild()) invalidateBase();
  filling = [r, c];
  const st = updateProgress();
  filling = null;
  persist(st ? st.pct : 0, isComplete(st));
  if (soon && !isComplete(st)) renderSoon();
  else render();
  if (isComplete(st) && !S.gameOver) finishGame(st!);
}

/** Terminé se compte en cases, jamais en pourcentage : celui-ci est un affichage. */
function isComplete(st: Progress | null): boolean {
  return !!st && st.done >= st.todo;
}

/**
 * A symbol has just had its last cell filled — nine of these per sheet, spread over the whole
 * session whatever order they are painted in. That is the point of them: revealing the drawing
 * used to be the only reward, so a player who started with the most covering symbol spent it in
 * the first minute and painted the rest for nothing.
 *
 * Sized between a tap and the ending: the burst is a notch wider than a filled zone's, made of
 * the symbol that just ran out. No sound of its own — the fill that emptied the symbol already
 * played one, and this is not a second event to announce, just the biggest consequence of that
 * same tap. It fires at the cell the fill ended on, which is where the eye already is.
 */
function symbolDone(code: number, r: number, c: number): void {
  buzz('medium');
  const cx = c * S.cellW + S.cellW / 2;
  const cy = r * S.cellH + S.cellH / 2;
  animRing(cx, cy);
  animBurst(cx, cy, 14, code, 5.2);
  // Le verrou vient de passer au symbole suivant (onCodeProgress, dans la même image), et son
  // repère se dessinait aussitôt : ses cases se hachuraient en rose, PUIS la caméra reculait, et
  // les hachures disparaissaient pour revenir au retour — « je termine la dernière case d'un
  // type, les cases suivantes se hachurent en rose, puis ensuite il y a l'effet de dézoom »
  // (Vincent, 2026-10-31). La récolte ne masquait le repère qu'une fois lancée : 0,42 s plus tard
  // sur un tap (HARVEST_LAND), au lever du doigt sur un balayage. Il est donc retenu dès ici, et
  // rendu en fondu à la fin de la récolte, quelle qu'en soit l'issue (cf. startHarvest).
  if (!noMotion()) anim.haloHeld = true;
  // The light over the whole harvest waits for the hand to leave the sheet — see startHarvest.
  if (touching) pendingHarvest = () => startHarvest(code, r, c);
  else startHarvest(code, r, c);
}

/** Combien d'étincelles au plus. Au-delà, une sur n, prises au hasard : à 2,6 px la case, un
 *  millier d'étoiles n'est plus une pluie, c'est un voile — et c'est un millier de chemins par
 *  image. */
const TWINKLES_MAX = 220;
/** Le temps que met la vague d'étincelles à traverser la feuille, de la case finie au plus loin.
 *  Avec la vie d'une étincelle (TWINKLE_MS), la vague tient dans FLARE_MS : la caméra repart
 *  quand la dernière s'éteint. */
const TWINKLE_SPREAD_MS = 760;

/**
 * Les étincelles de la récolte : chaque case du symbole fini scintille, en vague depuis celle qui
 * l'a terminé, à partir de `at` (cf. drawTwinkles dans render.ts). TOUTES ses cases et non celles
 * de l'écran : la caméra vient de reculer sur la feuille entière pour les montrer.
 */
function harvestTwinkles(code: number, r0: number, c0: number, at: number): void {
  const cells: Array<[number, number]> = [];
  for (let r = 0; r < S.gridH; r++)
    for (let c = 0; c < S.gridW; c++) if (S.target[r][c] === code) cells.push([r, c]);
  const keep = Math.min(1, TWINKLES_MAX / Math.max(1, cells.length));
  const x0 = (c0 + 0.5) * S.cellW;
  const y0 = (r0 + 0.5) * S.cellH;
  const picked = cells
    .filter(() => Math.random() < keep)
    .map(([r, c]) => {
      const x = (c + 0.5) * S.cellW;
      const y = (r + 0.5) * S.cellH;
      return { x, y, d: Math.hypot(x - x0, y - y0) };
    });
  const dmax = Math.max(1, ...picked.map((p) => p.d));
  animTwinkles(
    picked.map((p) => ({
      x: p.x,
      y: p.y,
      // Un rien de hasard dans l'ordre, sinon c'est un radar qui balaie.
      t0: at + (p.d / dmax) * TWINKLE_SPREAD_MS + Math.random() * 120,
      s: 0.7 + Math.random() * 0.6,
      rot: Math.random() * Math.PI,
    })),
  );
}

/** A finger is on the sheet. Not S.gesture, which only covers a pan or a pinch: a paint stroke
 *  leaves the layers alone and so never sets it, and a stroke is exactly what finishes a symbol
 *  most of the time. */
let touching = false;
/**
 * A milestone reached mid-stroke: the beat is held until the finger leaves. Its camera cannot pull
 * back from under a hand that is painting, and a sweep is over in a moment — the chord, the ring
 * and the gerbe have already fired at the cell, so nothing about the milestone is late, only the
 * light that needs the whole sheet in frame.
 *
 * The last one wins. Two symbols emptied by one sweep is two chords and one light, and the light
 * that plays is the one that just happened.
 */
let pendingHarvest: (() => void) | null = null;

/*
 * IL N'Y A PLUS DE SEUIL DE VISIBILITÉ, et c'est un revirement.
 *
 * Le recul de caméra était conditionnel : si la moitié du symbole était déjà à l'écran
 * (`HARVEST_SEEN = 0.5`), on jugeait le vol inutile et la lumière courait sur place. L'intention
 * était bonne — ne pas déplacer la caméra pour rien — et le résultat mauvais : le palier avait
 * DEUX FORMES, et le joueur ne pouvait pas savoir laquelle il allait obtenir. Mesuré sur une
 * feuille 30 × 30 avec un amas de neuf cases, zoom 3,5, part visible 1,00 : le palier passait
 * sans un seul mouvement de caméra, là où le même palier atteint dix cases plus loin reculait
 * sur la feuille entière. Un temps fort est un RYTHME, pas une optimisation ; il doit se
 * reconnaître, et pour ça il doit toujours se dérouler pareil.
 *
 * Ce qui reste économe, sans condition à écrire : `flyToWhole()` rend 0 quand la feuille est
 * déjà cadrée en entier (cf. render.ts), donc le joueur qui joue dézoomé ne paie aucun vol — et
 * c'était de toute façon le seul cas où le seuil avait raison.
 */
/** Pause between the last cell landing and the light leaving it, so the beat reads as cause and
 *  then effect: you place the cell, it blooms in, and only then does the light come out of it.
 *  Long enough to let the fill's own bloom finish (POP_MS), plus the breath after it. */
const HARVEST_LAND = 420;
/** And after the camera has settled, since the same reading applies once it has travelled. */
const HARVEST_HOLD = 160;

/**
 * « La récolte » — every cell of the symbol just finished lights up, in a single pass of light
 * leaving the cell that completed it.
 *
 * The milestone used to be entirely local: a ring and a gerbe on one cell, three pixels wide on a
 * phone at 120 cases. But what was accomplished is spread over the whole sheet, and once the
 * symbol is done its cells become indistinguishable from the rest of the graphite — the player
 * never got to see what they had painted. This shows it, in the shape it gives the picture, and
 * then gives it back to the drawing.
 *
 * Zoomed in, that harvest is mostly off-frame, so the camera pulls back onto the sheet and hands
 * the viewport back afterwards — the same move the ending's replay makes, and for the same reason:
 * a reward watched through a keyhole shows nothing. It is a beat and not a relocation, so the
 * player's zoom comes back untouched; where it is handed back is handBack's business, since the
 * lock has moved on to another symbol in the meantime.
 *
 * Under reduced motion, no light and no travel — this is a flourish, and a flourish is what someone
 * asking for less motion turned down. The framing is not: see handBack.
 *
 * LA LUMIÈRE EST DEVENUE DES ÉTINCELLES (2026-10-31). Le recul et le retour restent ; ce qui se
 * passe entre les deux a changé. La feuille passait dans l'ombre pendant qu'un lavis rose
 * traversait les cases du symbole, puis tout se rallumait, et le repère du symbole suivant
 * surgissait d'un coup au retour — relevé image par image sur le chien en peinture nuancée, et
 * Vincent : « ça clignote vers les futurs types de case puis ça clignote, c'est vraiment pas
 * top ». Sur un alphabet de peinture, les cases d'un symbole sont des touches éparses sous les
 * couleurs voisines, et le lavis n'y dessinait aucune forme. Une pluie d'étoiles dorées sur ces
 * mêmes cases (harvestTwinkles) montre la même chose sans rien éteindre ; la lumière se rebranche
 * par HARVEST_LIGHT dans render.ts. Une première version sans recul du tout, étoiles sur les
 * seules cases visibles, a été jugée trop discrète : on voulait voir TOUT ce qu'on avait fait.
 */
function startHarvest(code: number, r: number, c: number): void {
  if (noMotion()) {
    anim.haloHeld = false;
    handBack();
    invalidateHalo();
    // Le seul appel du chemin sans mouvement : le palier atteint en pleine caresse arrive par
    // pointerup, qui rend la main sans redessiner (voir pendingHarvest).
    render();
    return;
  }
  // Le cadrage du joueur, mis de côté AVANT le vol : c'est celui-là qu'on lui rendra.
  const avant = { ...S.viewport };
  const vol = flyToWhole();
  // `back` garde son sens exact — « on a emprunté un cadrage, voilà ce qu'il faut rendre » — donc
  // il reste nul quand aucun vol n'a eu lieu : il n'y a alors rien à défaire (cf. FlareEnd).
  const back = vol ? avant : null;
  // Sans vol, la lumière garde son temps de respiration : la case se pose, elle éclot, PUIS la
  // lumière en sort. Avec un vol, c'est l'arrivée de la caméra qui joue ce rôle.
  const delay = vol ? vol + HARVEST_HOLD : HARVEST_LAND;
  // The highlight of the symbol the lock has just moved on to, and the zone plates the pull-back
  // is about to bring on screen, both stand down for the beat: see drawHaloOn and buildZones.
  invalidateHalo();
  harvestTwinkles(code, r, c, animNow() + delay);
  animFlare(code, r, c, delay, (how) => {
    // Ce qu'on fait du cadrage, cf. FlareEnd dans anim.ts.
    //
    // « handback » : la récolte est allée jusqu'au bout. `back` nul, c'est qu'elle n'a pas eu à
    // reculer, et handBack recadre quand même sur des cases à faire — c'est tout son intérêt.
    // Le repère retenu depuis la dernière case (cf. symbolDone) se lève, en fondu, quelle que
    // soit l'issue — pendant le retour quand il y en a un, au lieu de surgir à l'arrivée.
    anim.haloHeld = false;
    anim.haloIn = animNow();
    if (how === 'handback') handBack(back);
    // Interrompue avant son premier éclat : les étoiles déjà programmées tomberaient sur le
    // cadrage qu'on vient de rendre, une fête que personne n'a demandée.
    if (how === 'undo') anim.twinkles.length = 0;
    // « undo » : interrompue avant son premier éclat, elle n'a RIEN montré. Elle rend donc le
    // cadrage tel qu'elle l'a pris, et d'un coup — pas de vol, la main est déjà sur la feuille.
    // Pas de handBack ici : recadrer sur du travail à faire est un service qu'on rend à un joueur
    // qui regarde, pas à un doigt qui vient de se poser et qui a son propre projet.
    //
    // Les couches en cache n'ont rien à craindre de ce saut : render() invalide de lui-même à la
    // fin d'un vol (le `wasFlying && !flying` en tête), et animFlareEnd vient de couper celui-ci.
    let saut = false;
    if (how === 'undo' && back) {
      S.viewport.z = back.z;
      S.viewport.x = back.x;
      S.viewport.y = back.y;
      saut = true;
    }
    // Dans tous les cas : le repère s'était tu pendant la récolte (cf. drawHaloOn), il revient
    // sur le symbole que le verrou a pris entre-temps.
    invalidateHalo();
    render();
    return saut;
  });
  render();
}

/**
 * Rend la feuille au joueur après un palier — à son zoom, mais posé sur du travail à faire.
 *
 * Le palier vide un symbole et le verrou passe au suivant (voir onCodeProgress) : le cadrage qu'on
 * quitte est précisément celui dont on vient de finir les cases, et le rendre tel quel répond à la
 * récompense par une page blanche. handBackView() cherche donc, au zoom qu'on rend, la fenêtre la
 * plus proche qui tienne des cases du nouveau symbole — et se tait quand celle du joueur en tient
 * déjà, auquel cas on lui rend exactement la sienne.
 *
 * Le verrou est lu ICI et pas à l'armement de la récolte : le palier atteint en pleine caresse
 * attend que le doigt se lève (pendingHarvest), et entre les deux le joueur a pu choisir un autre
 * symbole dans la palette. Celui qui compte est celui sur lequel il repart.
 *
 * Cadrer du travail à faire est un service et pas un ornement — sans mouvement, animFlyTo pose le
 * viewport d'un coup, exactement comme pour un tap sur une zone. Ce que la préférence refuse, c'est
 * le voyage ; se retrouver devant une page vide, c'est le jeu qui ne répond plus.
 */
function handBack(back?: { z: number; x: number; y: number } | null): void {
  const from = back ?? S.viewport;
  const to = handBackView(from, S.lockedSymbol) ?? back;
  if (to) animFlyTo(to.z, to.x, to.y);
}

/** Fills the cell that was tapped, and it alone. */
export function paintAt(r: number, c: number): void {
  // Rien ne peint pendant le rejeu : `filled` y est temporairement faux, et une case ajoutée
  // là allongerait la liste de taps que le rejeu est en train de parcourir.
  if (anim.replay) return;
  const m = S.target[r][c];
  if (m === 0) return; // background: nothing to paint
  if (S.lockedSymbol !== null && m !== S.lockedSymbol) {
    // Zen, and it is the default: a cell of another symbol costs STRICTLY nothing. Not a
    // softened penalty — no flash, no thud, no shake, no combo lost, no vibration. The tap
    // simply never happened. The sheet is the only thing that answers a gesture, and it has
    // nothing to say about a cell you didn't paint.
    //
    // It reads as unresponsive for exactly as long as it takes to notice the cell didn't fill,
    // which is the point: nothing was taken from you either. The player who wants the stakes
    // back turns the switch off and gets the paragraph below.
    if (settings().zen) return;
    // Locked symbol != the cell's. Deliberately restrained: the cell flashes and the sheet shakes,
    // nothing more. An expanding red ring and a haptic buzz on top made a plain misaim feel
    // like a penalty, and misaiming is constant on cells a few pixels wide. No sound either: the
    // one sound this game makes now is a cell filling, and a mistake is the one thing that isn't.
    S.mistake = { r, c, t0: animNow() };
    animShake();
    S.combo = 0;
    render();
    return;
  }
  if (!fillCell(r, c)) return;
  const zone: Array<[number, number]> = settings().flood ? floodFrom(r, c, m) : [[r, c]];

  const cx = c * S.cellW + S.cellW / 2;
  const cy = r * S.cellH + S.cellH / 2;
  animPop(zone, r, c);
  // Plus d'anneau concentrique sur un remplissage ordinaire : la presse porte désormais le
  // contact dans la case elle-même — l'écrasement et l'auréole d'encre — et l'anneau par-dessus
  // faisait deux fois la même annonce, la seconde par-dessus le dessin. Il reste au palier
  // (symbolDone), où il ne se produit que neuf fois dans la partie et où il célèbre autre chose.
  // The scraps grow with what was actually painted, capped so a big zone doesn't turn into
  // confetti; on the single cell of the default rule the count is the fixed handful it always was.
  animBurst(cx, cy, Math.min(12, 3 + zone.length), m);
  pop(m, Math.min(7, S.combo - 1));
  buzz(S.combo > 2 ? 'medium' : 'light');
  afterFill(r, c);
}

/** Minimum gap between two pops of a stroke. A sweep fills several cells per frame and one pop
 *  each is a machine gun — throttled, the climbing combo turns the sweep into a rising run of
 *  brief, cheerful blips instead. */
const STROKE_NOTE_MS = 60;
let strokeNoteAt = 0;

/**
 * Même remède pour la gerbe, avec un intervalle plus long et moins d'éclats.
 *
 * Elle était absente du balayage, et pour une bonne raison : une par case, c'est trente gerbes
 * dans un geste d'une seconde — des confettis, pas un retour. Mais l'absence coûtait autre
 * chose, qui se voit à l'usage : le glissé était plus plat que le tap alors que c'est le geste
 * principal du jeu. Espacée, elle laisse une traînée d'éclats le long du passage du doigt au
 * lieu d'un feu d'artifice, et le nombre d'éclats vivants reste du même ordre qu'après un tap.
 */
const STROKE_BURST_MS = 110;
const STROKE_BURST_N = 4;
let strokeBurstAt = 0;

/**
 * One step of a swept stroke. Silently ignores anything that isn't a cell of the locked symbol
 * still to fill: a stroke can therefore NEVER be a mistake — the worst a misread gesture can do
 * is fill cells that were going to be filled anyway. That is what makes deciding paint-vs-pan
 * from the cell under the finger safe despite the rule being invisible.
 *
 * `flood` applies here exactly as it does to a tap: the switch describes what filling a cell
 * DOES, not which gesture asked for it — a stroke that painted cell by cell while a tap took the
 * whole zone would read as a bug. With it on the sweep mostly ends on its first cell, the zone
 * having swallowed everything the rest of the gesture would have crossed; that is what the
 * switch asks for, and whoever wants the stroke to matter leaves it off.
 *
 * L'anneau reste au tap : lui n'a pas de version espacée qui garde un sens — un anneau, c'est
 * l'onde d'UN contact, et un balayage n'en a pas un, il en a cent. La gerbe, elle, est rendue au
 * balayage mais espacée (STROKE_BURST_MS).
 */
function paintStrokeAt(r: number, c: number, sym: number | null): boolean {
  if (anim.replay) return false;
  if (sym === null || S.target[r][c] !== sym) return false;
  if (!fillCell(r, c)) return false;
  const zone: Array<[number, number]> = settings().flood ? floodFrom(r, c, sym) : [[r, c]];
  animPop(zone, r, c);
  const t = animNow();
  if (t - strokeNoteAt >= STROKE_NOTE_MS) {
    strokeNoteAt = t;
    pop(sym, Math.min(7, S.combo - 1));
    buzz('light');
  }
  if (t - strokeBurstAt >= STROKE_BURST_MS) {
    strokeBurstAt = t;
    animBurst(c * S.cellW + S.cellW / 2, r * S.cellH + S.cellH / 2, STROKE_BURST_N, sym);
  }
  return true;
}

/** Only a catalogue puzzle is saved; the demo image belongs to nobody. */
function persist(pct: number, done: boolean): void {
  if (persistOverride?.(pct, done)) return;
  if (S.puzzleId) saveProgress(S.puzzleId, S.puzzleFp, S.taps, pct, done, () => renderThumb());
}

/**
 * Une sauvegarde qui n'est pas celle de la feuille à l'écran : pendant la pièce d'une plus grande
 * image (src/piece.ts), la feuille ouverte est un découpage, et ce qu'on enregistre est la GRANDE
 * feuille, ses cases traduites. Rend vrai s'il a pris la main. Posé et retiré par piece.ts seul.
 */
let persistOverride: ((pct: number, done: boolean) => boolean) | null = null;
export function setPersistOverride(fn: ((pct: number, done: boolean) => boolean) | null): void {
  persistOverride = fn;
}

// ---------- the replay ----------
/**
 * A finished sheet draws itself again, in the order it was actually painted. The saved taps ARE
 * that order (see progress.ts), so the reward costs no new data and can't disagree with what
 * happened: what you watch is your own hand, sped up — where you started, the symbol you spent
 * an evening on, the corner you left for last.
 *
 * It plays at the end of a game and again every time a finished puzzle is reopened, which is
 * what makes a finished puzzle worth opening at all.
 *
 * Silent: the one sound this game makes is a cell filling under a player's own finger, and a
 * replay is the opposite of that — nobody is tapping.
 *
 * The whole thing is driven from the animation loop (animReplay), which hands us the cells to
 * reveal frame by frame; here we only own what a reveal MEANS — the sheet's state.
 */
export function replayReveal(from: number, to: number): void {
  revealTaps(from, to);
}

/**
 * Empties the sheet and lets it fill itself back up, then runs `end`.
 *
 * `filled` is emptied to the rule resetPlay() uses — a cell with nothing to paint was never
 * painted — and the reveal fills it back in, so the sheet ends up exactly as it started. The
 * taps themselves are never touched: they are the script, and the save is already written.
 *
 * With nothing to replay, or with motion off, the ending happens on the spot: this is a
 * flourish, and a flourish is precisely what a player who asked for less motion turned down.
 */
export function startReplay(end: () => void): void {
  const total = S.taps.length;
  if (!total || noMotion()) {
    end();
    return;
  }
  // The fill that just ended the game is still blooming in, and its cells are marked as drawn
  // live — they would hang on screen over an emptied sheet, and the frozen layer would keep
  // skipping them. Clearing the effects hands the replay a sheet with nothing left in flight.
  animResetGrid();
  for (let r = 0; r < S.gridH; r++)
    for (let c = 0; c < S.gridW; c++) S.filled[r][c] = S.target[r][c] === 0;
  const delay = flyToWhole();
  // Le rejeu est armé AVANT le premier rendu : buildZones() se tait pendant le rejeu, encore
  // faut-il qu'il le voie commencé. Rendre d'abord, c'est lui faire compter une feuille qu'on
  // vient de vider — et afficher une volée de plaques sur un dessin déjà terminé.
  animReplay(total, delay, end);
  invalidateLayers();
  render();
}

/**
 * Le bouton « Revoir » : montré quand il y a quelque chose à revoir et que rien ne joue.
 *
 * Trois conditions, et chacune répond à une question du joueur. `S.gameOver` : y a-t-il une fin
 * à revoir ? `anim.replay` et `anim.final` : est-ce que ça joue déjà ? Pendant la séquence le
 * bouton n'aurait rien à faire, et un bouton dont l'appui ne change rien apprend à ne plus le
 * regarder — c'est le raisonnement que portait le bouton « toute l'image », et il vaut ici aussi.
 *
 * Appelé aux instants où l'état change, jamais à chaque image : basculer un attribut soixante
 * fois par seconde relancerait un calcul de style pour rien.
 */
/** La rangée de fin occupait-elle sa place au dernier passage ? C'est ce que syncAgain() compare
 *  pour ne remesurer la feuille QUE quand la hauteur a vraiment bougé, jamais aux autres appels —
 *  repeindre les étoiles n'en change rien, et son contenu qui se dévoile non plus depuis que la
 *  place est réservée d'avance. */
let againHadPlace = false;

/**
 * La barre du bas — la palette, ou la rangée « Revoir » qui prend sa place — est une SURFACE
 * depuis que l'écran de jeu a perdu sa carte (cf. .bottombar dans style.css). Elle ne doit donc
 * pas MONTRER de matière quand elle n'a rien à porter, et il y a un instant où elle n'a rien :
 * entre la dernière case peinte et l'arrivée de la rangée, la palette est déjà vide — tous les
 * symboles sont faits, buildLegend() ne construit aucune pastille — et la rangée est encore
 * retenue par le rejeu puis par la carte de fin. Sans cette bascule, une bande blanche
 * s'installerait en bas de l'écran pendant toute la salve, juste au moment où le joueur regarde
 * son dessin.
 *
 * ELLE GARDE SA PLACE, EN REVANCHE, et c'est la correction du 2026-09-04. Elle s'escamotait
 * entièrement (`hidden`), donc la boîte de la feuille grandissait puis rétrécissait — mesuré sur
 * un Pixel 7a : la boîte passait de 742 à 830 px à la dernière case, le dessin descendait de
 * 44 px, puis la rangée arrivait 6,3 s plus tard (elle fait 104 px là où la palette en fait 74)
 * et le dessin remontait de 59 px. Cent trois pixels de voyage, dont le dernier saut tombe
 * exactement quand le joueur contemple son image finie.
 *
 * Désormais la place de la rangée est due dès la dernière case (cf. syncAgain), donc la barre
 * reste dans le flux d'un bout à l'autre de la séquence de fin : elle perd son fond, son bord et
 * son ombre le temps qu'elle est vide (`.bare`), et les retrouve quand la rangée s'affiche. Plus
 * une seule remesure entre les deux.
 *
 * La palette vide est cachée à part, pour ses 10 px de rembourrage vertical : ils pousseraient la
 * rangée vers le bas d'une barre qui, elle, a bien quelque chose à montrer.
 *
 * Aucun afterLayout() ici : ses appelants comparent déjà une hauteur avant et après.
 */
function syncBottomBar(): void {
  const empty = !$('legend').querySelector('.item');
  $('legend').hidden = empty;
  const bar = $('bottombar');
  const row = $('againRow');
  bar.hidden = empty && row.hidden;
  // Rien de visible à porter : la barre garde sa hauteur et devient transparente. Depuis que la
  // rangée se montre dès la fin (cf. syncAgain), ce cas ne se présente plus en fin de partie ; la
  // bascule reste pour l'instant où la palette se vide avant que la rangée ait sa place.
  bar.classList.toggle('bare', empty && (row.hidden === true || row.classList.contains('veiled')));
}

/**
 * L'état de la rangée de fin, en DEUX questions qui n'ont pas la même réponse :
 *
 *  - « a-t-elle droit à sa place ? » — oui dès que la partie est finie, y compris pendant le
 *    rejeu et la carte de fin. C'est ce qui fige la géométrie de l'écran pour toute la séquence.
 *  - « son contenu se montre-t-il ? » — oui dès la fin aussi, depuis le 2026-10-18, sauf
 *    « Revoir » : proposé au milieu du rejeu qu'il déclenche, il n'aurait rien à faire. Les
 *    étoiles et « Partager » attendaient avec lui, mais une feuille rouverte rejouait alors
 *    jusqu'à 10 s avant qu'on puisse la partager (cf. le corps de la fonction).
 *
 * Les deux étaient confondues, et c'est ce qui faisait sauter le dessin : la rangée entrait dans
 * le flux au moment même où elle s'affichait, six secondes après la fin de la partie.
 */
function syncAgain(): void {
  const place = S.gameOver;
  // LE CONTENU SE MONTRE DÈS LA FIN, sauf « Revoir » (2026-10-18). Toute la rangée attendait la
  // fin du rejeu et de la lumière finale, de peur que les étoiles détournent le regard de la salve
  // — mais une feuille ROUVERTE rejoue jusqu'à 10 s avant qu'on puisse la partager ou la noter, et
  // c'est souvent pour ça qu'on la rouvre (demandé en jouant). Seul « Revoir » reste voilé
  // pendant la séquence : là, il n'aurait toujours rien à faire. Voilé et non retiré, il garde sa
  // place et « Partager » ne glisse pas quand il arrive. Partager en plein rejeu le termine
  // d'abord (cf. le bouton dans wireUI) : l'image se fait sur les cases déjà révélées.
  const joue = !!anim.replay || !!anim.final;
  $('againRow').hidden = !place;
  $('againRow').classList.toggle('veiled', !place);
  $('againBtn').classList.toggle('veiled', joue);
  // UNE CRÉATION (src/creer.ts) n'a ni partage ni note : personne d'autre ne peut l'ouvrir, donc
  // un lien partagé mènerait sur « ce dessin n'est plus disponible », et une note n'informerait
  // personne. Posé AVANT la mesure qui suit, pour que la place prise par la rangée soit celle de
  // ce qu'elle montre vraiment.
  // Le partage, lui, revient pour une création en IMAGE ou en VIDÉO (cf. src/sharePanel.ts) — là
  // où c'est possible, ce qui est désormais partout, application comprise (cf. canShareFile).
  // Le test reste : il suffit que canShareFile() redise non quelque part pour recacher le bouton.
  const mine = isCreation(S.puzzleId);
  $('shareBtn').hidden = mine && !canShareFile();
  ($('stars').closest('.rate') as HTMLElement).hidden = mine;
  syncBottomBar();
  // Les étoiles vivent dans la même rangée : leur visibilité suit celle du bouton sans rien de
  // plus, seul leur état plein/vide a besoin d'être reposé à chaque fois que la rangée reparaît
  // (fin de partie, fin de rejeu, réouverture d'un puzzle déjà noté).
  for (const btn of $('stars').querySelectorAll<HTMLButtonElement>('.star')) {
    btn.classList.toggle('on', Number(btn.dataset.v) <= S.rating);
  }
  // La rangée vient de prendre ou de rendre sa place — et c'est bien la PLACE qu'on suit ici, pas
  // l'affichage : depuis que les deux sont distincts, seule la première fait bouger la hauteur de
  // la feuille. Même remède que buildLegend() pour la palette : remesurer seulement quand la
  // hauteur a vraiment changé.
  if (place !== againHadPlace) {
    againHadPlace = place;
    afterLayout();
  }
}

/** Rejoue la fin, et remet le bouton en place quand elle est finie. `animFinal` s'éteint tout
 *  seul au bout de FINAL_MS sans rappeler personne, d'où le délai plutôt qu'un événement. */
function replaySequence(): void {
  // startReplay D'ABORD, la synchro ensuite : c'est lui qui pose `anim.replay`, et l'appeler
  // avant laissait le bouton visible pendant tout le rejeu — mesuré, pas supposé.
  startReplay(() => {
    animFinal();
    window.setTimeout(syncAgain, FINAL_MS + 80);
  });
  syncAgain();
  // Le rejeu peut être refusé — pas de taps enregistrés, animations coupées — et `end` part alors
  // tout de suite. Ce second appel rattrape ce cas : sans lui le bouton resterait caché.
  window.setTimeout(syncAgain, FINAL_MS + 80);
}

/** Reopening a finished puzzle: the same replay and the same closing beat, but no card and no
 *  fanfare. It was celebrated when it was earned. */
export function replayFinished(): void {
  replaySequence();
}

/** The ending itself, once the drawing is whole again: the grid dissolves, the light sweeps the
 *  ink, the symbols scatter, and only then does the card come up over it. */
/** Le temps que le passe-partout met à s'ouvrir (cf. paintMat dans render.ts : `fin * 2.4`
 *  sur FINAL_MS). La carte attend qu'il soit posé. */
const MAT_MS = Math.round(FINAL_MS / 2.4);

function celebrate(): void {
  animFinal();
  // La carte APRÈS le cadre, et non en même temps. Elle montait avec lui : le joueur recevait le
  // panneau de félicitations pendant que la lumière traversait encore son dessin, c'est-à-dire
  // pendant la seule seconde où on le lui montrait. Le commentaire de finishGame se méfie de ça
  // pour le rejeu ; il n'y avait aucune raison de le laisser arriver ici.
  //
  // Reconditionné à l'arrivée : le joueur peut avoir quitté la feuille entre-temps, et une carte
  // qui se poserait sur un puzzle qu'on vient d'ouvrir serait pire que pas de carte du tout.
  //
  // Ni quand la feuille de partage est ouverte : « Partager » se montre pendant le rejeu (cf.
  // syncAgain), et l'appuyer termine ce rejeu, donc lance cette célébration — la carte se posait
  // sous la feuille et attendait le joueur à sa fermeture, arrivée sans qu'il l'ait vue venir.
  window.setTimeout(() => {
    if (S.gameOver && $('shareSheet').getAttribute('aria-hidden') !== 'false') {
      // Le dernier mot du renard, sur la seule feuille qui le porte (cf. setFinishedFox).
      $('finishedFox').hidden = !finishedFoxFor || S.puzzleId !== finishedFoxFor;
      $('finished').classList.add('on');
    }
  }, MAT_MS);
  // final burst: from the center, with the symbols actually present
  const codes: number[] = [];
  for (let r = 0; r < S.gridH; r += 3)
    for (let c = 0; c < S.gridW; c += 3) if (S.target[r][c] > 0) codes.push(S.target[r][c]);
  for (let i = 0; i < 26; i++) {
    const cd = codes.length ? codes[Math.floor(Math.random() * codes.length)] : 1;
    setTimeout(() => animBurst(S.pxW / 2, S.pxH * 0.62, 1, cd, 5.6), i * 26);
  }
  // Le bouton « Revoir » apparaît aussi après la PREMIÈRE fin, pas seulement aux réouvertures :
  // sans ça, celui qui vient de terminer devait quitter la feuille et y revenir pour avoir le
  // droit de la revoir.
  window.setTimeout(syncAgain, FINAL_MS + 80);
}

/** Sous dix joueurs, « avec toi, 3 joueurs » souligne surtout le vide : on dit plutôt que le
 *  joueur est parmi les premiers — c'est vrai, et c'est la bonne nouvelle de ce chiffre-là. */
const PEERS_MIN = 10;

/**
 * La ligne « Avec toi, 1 244 joueurs ont terminé ce dessin » de la carte de fin : qu'on n'est pas
 * seul à jouer, dit à l'instant où on regarde son dessin. Ce qui reste de la ligue, sans rang.
 *
 * Cachée tant que la réponse n'est pas là, et pour toujours si elle ne vient pas (hors ligne, pas
 * encore de compte — il naît à la première sauvegarde, cf. account.ts) : la carte reste alors
 * celle d'avant, sans trou. Le serveur compte les AUTRES ; « toi » est ajouté ici (cf.
 * getFinishers dans backend/src/routes/community.ts).
 */
async function showPeers(id: string): Promise<void> {
  $('finishedPeers').hidden = true;
  // Une création n'a qu'un joueur, et il le sait.
  if (isCreation(id)) return;
  const token = await currentUser();
  if (!token) return;
  let others: number;
  try {
    others = await fetchFinishers(token, id);
  } catch {
    return;
  }
  // Reconditionné à l'arrivée, comme la carte elle-même (cf. celebrate) : le joueur peut avoir
  // ouvert une autre feuille pendant que la réponse voyageait.
  if (S.puzzleId !== id || !S.gameOver) return;
  const n = others + 1;
  const key =
    others === 0
      ? 'game.peers.none'
      : n < PEERS_MIN
        ? 'game.peers.first'
        : openedTodaysDaily()
          ? 'game.peers.daily'
          : 'game.peers.sheet';
  // Le nombre en gras, posé par le dictionnaire là où sa langue le met. Le texte vient des
  // dictionnaires et le nombre d'Intl : rien d'extérieur n'entre dans ce innerHTML.
  $('finishedPeersText').innerHTML = t(key, {
    n: `<b>${new Intl.NumberFormat(currentLang()).format(n)}</b>`,
  });
  $('finishedPeers').hidden = false;
}

/**
 * Une fin qui n'est pas la fin : la pièce d'une plus grande image (src/piece.ts) remplace la
 * carte de fin de la petite feuille par le recul vers la grande. Rend vrai s'il a pris la main ;
 * finishGame() s'arrête alors là.
 */
let finishOverride: (() => boolean) | null = null;
export function setFinishOverride(fn: (() => boolean) | null): void {
  finishOverride = fn;
}

/** Une feuille vient d'être finie — l'entonnoir de l'initiation y lit la fin du chien entier
 *  (cf. main.ts). Un crochet et pas un import : intro.ts importe ce module. */
let sheetFinished: ((id: string | null) => void) | null = null;
export function setOnSheetFinished(fn: ((id: string | null) => void) | null): void {
  sheetFinished = fn;
}

/**
 * La feuille dont la carte « Terminé » porte le dernier mot du renard (#finishedFox) — le chien de
 * l'initiation, posé par main.ts : ce module ne connaît pas intro.ts, qui l'importe. Relu à chaque
 * carte, pas posé à la fin du chien : une carte rejouée sur une autre feuille finie le garderait.
 */
let finishedFoxFor: string | null = null;
export function setFinishedFox(id: string | null): void {
  finishedFoxFor = id;
}

function finishGame(st: Progress): void {
  if (finishOverride?.()) return;
  sheetFinished?.(S.puzzleId);
  S.gameOver = true;
  S.justFinished = true;
  // Pour la demande d'avis, et rien d'autre : elle ne se posera qu'au prochain LANCEMENT, si le
  // joueur revient. Noté ici plutôt qu'à la sortie de la feuille parce que c'est ici que la
  // partie est finie — quitter le jeu sans repasser par le catalogue compte tout autant.
  markGameFinished();
  // Précharger maintenant, pas à la sortie de la feuille : le joueur regarde son dessin fini
  // pendant que ça charge, plutôt que d'attendre devant un catalogue qui tarde (cf. ads.ts).
  void prepareInterstitial();
  $('finishedSub').textContent = t('game.finishedSub', {
    todo: st.todo,
    w: S.gridW,
    h: S.gridH,
    alphabet: alphabetLabel(S.alphabetId).toLowerCase(),
  });
  // Pas pour le chien de l'initiation (finishedFoxFor) : tout nouveau joueur l'a en sauvegarde, et
  // compter ceux qui l'ont fini parcourt toutes ses lignes (l'index de `progress` ne porte que la
  // feuille) — la plus grosse lecture de la base, à chaque fin, pour une ligne que le mot du renard
  // remplace sur sa carte (revue du 2026-10-31).
  if (S.puzzleId && S.puzzleId !== finishedFoxFor) void showPeers(S.puzzleId);
  // The replay first, the celebration after it — one sentence and not two things at once. The
  // card would otherwise cover the drawing at the very moment it is being drawn.
  startReplay(celebrate);
  // APRÈS startReplay, et c'est tout ce qui compte dans cet ordre : il vient de poser
  // `anim.replay`, donc la rangée prend sa place sans montrer son contenu. La feuille change
  // donc de hauteur ICI — à l'instant où la dernière case éclot et où le rejeu s'élance, quand
  // tout bouge déjà — et plus six secondes plus tard sous les yeux d'un joueur qui regarde une
  // image immobile.
  syncAgain();
}

// ---------- pointer ----------
/** Common minimum of PointerEvent and WheelEvent: both are only used to locate. */
interface PointerLike {
  clientX: number;
  clientY: number;
}

/** Cell under a point already expressed relative to the canvas — the form a stroke needs, since
 *  it samples positions it computed rather than positions an event reported. */
function cellAtLocal(x: number, y: number): [number, number] | null {
  const gx = (x - S.viewport.x) / S.viewport.z;
  const gy = (y - S.viewport.y) / S.viewport.z;
  const c = Math.floor(gx / S.cellW);
  const r = Math.floor(gy / S.cellH);
  if (r < 0 || c < 0 || r >= S.gridH || c >= S.gridW) return null;
  return [r, c];
}

/**
 * Tolerance radius around a gesture, in SCREEN pixels — not in cells.
 *
 * In cells it would grow with the zoom: at 12x a cell is two centimetres wide and a tap two
 * centimetres away from one would fill it. And zoomed in is exactly where nobody needs help.
 * Expressed on the screen and capped by the cell, the tolerance fades out on its own as soon
 * as aiming becomes comfortable, which is the whole intent.
 */
const TOL_PX = 9;

/** Tolerance on a swept stroke as well as on a tap. The risky half: `paintSegment` samples
 *  every half cell, so the stroke fills a band two to three cells wide instead of the cells it
 *  actually crosses, and it picks up the isolated ones a finger deliberately swept around. It
 *  can read as a brush that stays magnetically stuck to a thin diagonal, or as a sheet that
 *  paints itself — one constant either way. */
const TOL_STROKE = true;

/**
 * The cell a gesture at (x, y) should fill: the one under it, or — when that one is not the
 * locked symbol's — the nearest one that is, within TOL_PX. Cells are a few pixels wide and a
 * finger is seven millimetres: a near miss meant the cell next door, and the game may as well
 * say so.
 *
 * Three things this gets right and a naive "look at the eight neighbours" would not:
 *
 * - The distance is measured to the cell's RECTANGLE, not to its centre. Otherwise a tap in the
 *   corner of a perfectly valid cell could be handed to a neighbour whose centre happens to be
 *   nearer — the finger was on the right cell and the game moved it. Distance zero inside, so
 *   the cell touched wins by construction, and ties go to the closest as a plain consequence.
 * - The radius is a true disc, where the eight neighbours are one cell away sideways and 1.41
 *   diagonally.
 * - It reveals nothing. Painting is gated on `zonesOnScreen()`, which flips at the same
 *   DETAIL_MAX_CELLS as the codes and the marks: whenever this can act, every remaining cell of
 *   the locked symbol is already lit on screen. It forgives the aim, it doesn't hand out the
 *   answer.
 *
 * Off in expert mode, and deliberately on the same switch as zen rather than a sixth one: both
 * say "the game forgives a misaim", and with the tolerance on, a fault would only ever fire
 * where no cell of the symbol is within nine pixels — in a dense region, never. Zen off would
 * have kept its thud and lost its meaning.
 */
function toleratedCell(x: number, y: number): [number, number] | null {
  const rc = cellAtLocal(x, y);
  if (!rc) return null;
  const lock = S.lockedSymbol;
  if (lock === null) return rc; // nothing aimed at: no near miss to speak of
  if (!S.filled[rc[0]][rc[1]] && S.target[rc[0]][rc[1]] === lock) return rc;
  if (!settings().zen) return rc;
  const z = S.viewport.z;
  const R = Math.min(S.cellW, S.cellH, TOL_PX / z); // screen pixels -> grid units
  const gx = (x - S.viewport.x) / z;
  const gy = (y - S.viewport.y) / z;
  const r0 = Math.max(0, Math.floor((gy - R) / S.cellH));
  const r1 = Math.min(S.gridH - 1, Math.floor((gy + R) / S.cellH));
  const c0 = Math.max(0, Math.floor((gx - R) / S.cellW));
  const c1 = Math.min(S.gridW - 1, Math.floor((gx + R) / S.cellW));
  let best: [number, number] | null = null;
  let bestD = R;
  for (let r = r0; r <= r1; r++)
    for (let c = c0; c <= c1; c++) {
      if (S.filled[r][c] || S.target[r][c] !== lock) continue;
      const dx = Math.max(c * S.cellW - gx, 0, gx - (c + 1) * S.cellW);
      const dy = Math.max(r * S.cellH - gy, 0, gy - (r + 1) * S.cellH);
      const d = Math.hypot(dx, dy);
      if (d < bestD) {
        bestD = d;
        best = [r, c];
      }
    }
  return best ?? rc;
}

function cellAt(e: PointerLike): [number, number] | null {
  const rect = S.sheet.getBoundingClientRect();
  return cellAtLocal(e.clientX - rect.left, e.clientY - rect.top);
}

/** Hold still this long on a paintable cell and the stroke becomes a pan. The escape hatch for
 *  the one case the rule below can't serve: a viewport filled edge to edge with your own symbol,
 *  where there is nowhere left to start a drag. Long enough that hesitating before a sweep still
 *  paints; a plain tap is unaffected either way, since it fills on release without moving. */
const HOLD_TO_PAN_MS = 350;

/**
 * La feuille ne prend aucun doigt : posé par la mise en scène de fin de la pièce d'initiation
 * (cf. swapToBig dans piece.ts), le temps du recul et du renard. Refusé dès la pose : un pointeur
 * que `pointerdown` n'a pas enregistré ne peint ni ne déplace rien ensuite (release l'ignore).
 */
let sheetLocked = false;
export function setSheetLocked(v: boolean): void {
  sheetLocked = v;
}

function wirePointer(): void {
  // One finger does everything: tap to fill, sweep to paint, drag to pan. Two fingers pinch to
  // zoom. We therefore track every active pointer, not just the first one.
  const active = new Map<number, { x: number; y: number }>();
  const ptr = { moved: false, sx: 0, sy: 0, ox: 0, oy: 0, px: 0, py: 0 };
  /** What the finger down on the sheet is for, decided once and never revised mid-stroke.
   *  'guide' est le troisième : le doigt s'est posé sur la boussole, qui n'est ni de la peinture
   *  ni de la saisie — voir plus bas. */
  let stroke: 'paint' | 'pan' | 'guide' = 'pan';
  /**
   * Le symbole que peint le balayage en cours : celui qui était verrouillé quand le doigt s'est
   * posé, et lui seul jusqu'au lever.
   *
   * Lire le verrou à chaque case ne marchait plus quand le balayage finit son symbole : le verrou
   * passe au suivant dans la même image (onCodeProgress), et la suite du MÊME geste peignait les
   * cases du nouveau symbole — sans repère pour les montrer (il est retenu jusqu'à la fin de la
   * récolte, cf. symbolDone), et avant même que la récolte ait commencé, puisqu'elle attend le
   * doigt levé. Vincent, 2026-10-31 : « la fin de mon geste est dans le type de case suivant, la
   * zone se remplit alors que l'effet n'a pas démarré ». Un geste, un symbole.
   */
  let strokeSym: number | null = null;
  /** La cible que la boussole désignait quand le doigt s'est posé dessus. Non nulle pendant tout
   *  le geste : c'est ce qui en fait un BOUTON et non un tap de plus sur la feuille. */
  let onGuide: [number, number] | null = null;
  let holdTimer = 0;
  // The pinch keeps track of the TWO pointers that opened it: without this, putting
  // down a third finger then lifting one of the first two would shift the calculation
  // onto a different pair, and thus a different spread — the zoom would jump.
  let pinch: { a: number; b: number; d0: number; z0: number; cx: number; cy: number } | null = null;

  const local = (e: PointerLike) => {
    const r = S.sheet.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  /** Spread and midpoint of the pinching pair, or null if one of the two was lifted. */
  const pinchPair = () => {
    if (!pinch) return null;
    const a = active.get(pinch.a);
    const b = active.get(pinch.b);
    if (!a || !b) return null;
    return {
      d: Math.hypot(a.x - b.x, a.y - b.y),
      mx: (a.x + b.x) / 2,
      my: (a.y + b.y) / 2,
    };
  };
  /** Re-anchors the drag on a given finger: from then on, its movements are measured
   *  from ITS current position. */
  const anchor = (p: { x: number; y: number }) => {
    ptr.sx = p.x;
    ptr.sy = p.y;
    ptr.ox = S.viewport.x;
    ptr.oy = S.viewport.y;
  };
  const endGesture = () => {
    S.gesture = false;
    // Back to crisp: layers are rasterized at the final viewport — except the cells' layer after
    // a plain pan, which is shifted rather than redrawn (cf. shiftBase in render.ts).
    settleGesture();
    render();
  };

  /**
   * Paint or navigate, decided from what is UNDER the finger when it lands — the same rule the
   * colouring apps use, and the reason one finger can do both without a mode button. It works
   * because the cells that paint are a minority of the sheet, and because they are the ones the
   * halo layer is already lighting up: the affordance is on screen before the rule applies.
   *
   * Everything else pans, including a cell of another symbol — the gesture that costs a `thud`
   * today stops being a punishment and becomes the way to move around.
   */
  const strokeKind = (e: PointerLike): 'paint' | 'pan' => {
    // Sweeping is meaningless without something to sweep FOR: with no symbol locked almost every
    // cell would paint, and there would be nowhere left to start a pan.
    if (S.lockedSymbol === null || anim.replay) return 'pan';
    // Zoomed out a cell is a few pixels wide: a tap already flies to a zone rather than filling,
    // and a stroke would scribble blind. Same threshold, no new rule to learn.
    if (zonesOnScreen()) return 'pan';
    const rc = cellAt(e);
    if (!rc) return 'pan';
    const [r, c] = rc;
    return !S.filled[r][c] && S.target[r][c] === S.lockedSymbol ? 'paint' : 'pan';
  };

  /**
   * Fills along the path travelled since the last event, not just at its end. A fast sweep only
   * produces a handful of pointermove events, and sampling their endpoints alone would leave
   * whole cells untouched between two frames — the stroke would come out dotted.
   *
   * Half a cell per sample: enough that no cell can be jumped over, cheap enough that a flick
   * across the screen is a few dozen lookups on already-filled cells.
   */
  const paintSegment = (to: { x: number; y: number }): void => {
    const step = Math.max(2, Math.min(S.cellW, S.cellH) * S.viewport.z * 0.5);
    const dx = to.x - ptr.px;
    const dy = to.y - ptr.py;
    const n = Math.max(1, Math.ceil(Math.hypot(dx, dy) / step));
    // Last cell the segment actually painted, not its endpoint: that is where a symbol running
    // out has to be celebrated, and the endpoint of a fast sweep is often past the sheet's cells.
    let hit: [number, number] | null = null;
    for (let i = 0; i <= n; i++) {
      const px = ptr.px + (dx * i) / n;
      const py = ptr.py + (dy * i) / n;
      const rc = TOL_STROKE ? toleratedCell(px, py) : cellAtLocal(px, py);
      if (rc && paintStrokeAt(rc[0], rc[1], strokeSym)) hit = rc;
    }
    ptr.px = to.x;
    ptr.py = to.y;
    // One render for the whole segment, whatever it crossed — and at most one per frame, cf.
    // afterFill.
    if (hit) afterFill(hit[0], hit[1], true);
  };

  S.sheet.addEventListener('pointerdown', (e) => {
    if (sheetLocked) return;
    // Le cadrage que ce doigt va lire est celui d'après le dernier rendu demandé (cf.
    // renderPending) : un deuxième doigt qui ouvre un pincement prend son point fixe dessus.
    renderPending();
    animPoke(); // a finger on the sheet brings the zone counts back — see platesAlpha()
    // La récolte cède à la main, et de deux façons selon ce qu'elle avait eu le temps de montrer
    // (cf. FlareEnd dans anim.ts) :
    //
    //  - sa lumière avait commencé : le joueur agit sur la vue d'ensemble qu'il a sous les yeux,
    //    on n'y touche pas — depuis là un tap cadre une zone, donc le geste dit lui-même où aller ;
    //  - elle n'avait pas commencé : le recul de la caméra était en cours, RIEN n'avait été montré,
    //    et le joueur repose le doigt sur une feuille qui s'éloigne pour une raison qu'il ignore.
    //    Le cadrage emprunté revient d'un coup, et ce doigt-ci ne peindra pas — voir plus bas.
    //
    // Ce second cas est le défaut le plus fréquent de la récolte : entre la dernière case et le
    // premier éclat il y a près de six dixièmes de seconde (le vol de recul, puis HARVEST_HOLD),
    // et à la cadence du jeu le doigt est déjà revenu. Il détruisait la récompense sans savoir
    // qu'elle existait ET repartait d'un zoom que personne n'avait choisi.
    //
    // `saut` dit que le cadrage a réellement bougé d'un coup. Ce n'est pas déductible de
    // l'intention : une récolte qui n'avait pas eu à reculer (son symbole tenait déjà à l'écran)
    // n'a rien à défaire, et ce doigt-là vise juste. C'est animFlareEnd qui le sait.
    const saut = animFlareEnd(anim.flare !== null && !flareLit() ? 'undo' : 'keep');
    touching = true;
    try {
      S.sheet.setPointerCapture(e.pointerId);
    } catch {
      /* not critical */
    }
    const p = local(e);
    active.set(e.pointerId, p);
    if (active.size === 1) {
      ptr.moved = false;
      anchor(p);
      ptr.px = p.x;
      ptr.py = p.y;
      stroke = strokeKind(e);
      strokeSym = S.lockedSymbol;
      // Le cadrage vient de sauter sous ce doigt : ses coordonnées visaient l'image d'AVANT, celle
      // que la récolte était en train de quitter. Le laisser peindre lui ferait remplir une case
      // qu'il n'a pas visée, et le laisser taper cadrerait une zone au hasard. Il ne peut plus que
      // saisir la feuille — ce qui est de toute façon ce qu'on fait d'une image qui a bougé.
      if (saut) {
        stroke = 'pan';
        ptr.moved = true;
      }
      /**
       * La boussole est un BOUTON, et cela se décide ici — pas au relâchement.
       *
       * Elle était traitée comme un tap ordinaire, donc soumise au seuil de 6 px qui distingue le
       * tap du geste : un pouce qui roule de sept pixels en appuyant ne ratait pas la cible, il
       * l'atteignait et récoltait un pan de sept pixels. Rien à l'écran n'expliquait le refus, et
       * c'est ce que les joueurs décrivaient comme « je n'arrive pas à appuyer dessus ».
       *
       * Le geste né sur elle ne peut donc plus rien faire d'autre : ni peindre (la case dessous
       * peut être du symbole verrouillé quand le bord la coupe — le cas fréquent, puisque la
       * boussole se pose justement au bord), ni saisir la feuille, ni basculer en saisie au bout
       * de 350 ms. Il ne reste que le vol, ou rien.
       *
       * APRÈS `saut` : quand la récolte vient de rendre son cadrage, l'image a bougé sous le
       * doigt et la boussole avec elle ; ce doigt-là ne vise plus ce qu'il croit.
       */
      if (!saut) {
        onGuide = guideHit(p.x, p.y);
        if (onGuide) stroke = 'guide';
      }
      if (stroke === 'paint') {
        holdTimer = setTimeout(() => {
          if (ptr.moved) return;
          stroke = 'pan';
          buzz('light'); // the switch has to be felt, nothing on screen says it happened
        }, HOLD_TO_PAN_MS) as unknown as number;
      }
    } else if (active.size === 2 && !pinch) {
      const [a, b] = [...active.keys()];
      pinch = { a, b, d0: 0, z0: S.viewport.z, cx: 0, cy: 0 };
      const m = pinchPair()!;
      // content point under the midpoint of the fingers: that's the one kept still
      pinch.d0 = m.d;
      pinch.cx = (m.mx - S.viewport.x) / S.viewport.z;
      pinch.cy = (m.my - S.viewport.y) / S.viewport.z;
      ptr.moved = true; // a pinch is never a tap
      // A second finger means "navigate": whatever the first one was doing, it stops painting.
      stroke = 'pan';
      onGuide = null;
      clearTimeout(holdTimer);
      S.gesture = true;
    }
  });

  S.sheet.addEventListener('pointermove', (e) => {
    // Before the branches, so it covers all of them: pinch, pan, paint, and the bare mouse
    // moving over the sheet — which is the desktop's way of asking for the counts back.
    animPoke();
    // Un seul relevé de la boîte du canevas par événement. `local()` passe par
    // getBoundingClientRect(), qui peut forcer un recalcul de mise en page, et il était appelé
    // deux fois pour un même déplacement — alors que le doigt émet des événements plus souvent
    // que l'écran n'affiche d'images. Nul quand le pointeur n'est pas des nôtres, pour ne rien
    // relever de plus qu'avant sur le survol à la souris (cellAt s'en charge, plus bas).
    const pos = active.has(e.pointerId) ? local(e) : null;
    if (pos) active.set(e.pointerId, pos);

    const m = pinchPair();
    if (pinch && m) {
      const { d, mx, my } = m;
      const z = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, (pinch.z0 * d) / pinch.d0));
      // the content under the fingers stays under the fingers: zoom and pan at once
      S.viewport.z = z;
      S.viewport.x = mx - pinch.cx * z;
      S.viewport.y = my - pinch.cy * z;
      // Demandé et non dessiné : un événement par doigt et par image, donc deux rendus par image
      // sinon — cf. renderSoon.
      renderSoon();
      return;
    }

    if (active.size === 1) {
      const p = pos ?? local(e);
      const dx = p.x - ptr.sx;
      const dy = p.y - ptr.sy;
      if (Math.abs(dx) + Math.abs(dy) > 6) ptr.moved = true;
      if (!ptr.moved) return;
      clearTimeout(holdTimer); // moving settles it: this stroke is what it was born as
      /**
       * Le doigt posé sur la boussole a le droit de bouger sans rien entraîner — c'est la dérive
       * ordinaire d'un pouce qui appuie, et elle se décide au relâchement.
       *
       * Mais SORTI du disque, ce n'est plus un appui : le geste redevient une saisie de la
       * feuille. Sans cette reprise, la boussole poserait une zone morte grande comme elle là où
       * l'on prend la page — et une zone morte qui se déplace toute seule le long du bord.
       *
       * On ré-ancre au passage : sans quoi le pan repartirait de l'endroit du POSER et la feuille
       * sauterait d'un coup de la largeur du disque au moment où le doigt en sort.
       */
      if (stroke === 'guide') {
        if (guideHit(p.x, p.y)) return;
        stroke = 'pan';
        onGuide = null;
        anchor(p);
        return;
      }
      if (stroke === 'paint') {
        paintSegment(p);
        return;
      }
      if (S.viewport.z > 1) {
        S.gesture = true;
        S.viewport.x = ptr.ox + dx;
        S.viewport.y = ptr.oy + dy;
        // Demandé : si une animation tourne, sa boucle dessine déjà cette image (cf. renderSoon).
        renderSoon();
      }
      return;
    }
    if (active.size > 0) return;

    // hover (mouse): highlights the cell under the cursor — exactly what a click would fill,
    // no more. Pointless when a symbol is locked: all its cells are already highlighted
    // permanently.
    if (S.lockedSymbol !== null) return;
    const rc = cellAt(e);
    const hit = rc && !S.filled[rc[0]][rc[1]] && S.target[rc[0]][rc[1]] > 0 ? rc : null;
    const sig = hit ? `${hit[0]},${hit[1]}` : '';
    if (sig !== S.hoverKey) {
      S.hover = hit;
      S.hoverKey = sig;
      invalidateHalo();
      render();
      animWake();
    }
  });

  S.sheet.addEventListener('pointerleave', () => {
    if (S.hover) {
      S.hover = null;
      S.hoverKey = '';
      invalidateHalo();
      render();
    }
  });

  const release = (e: PointerEvent, tap: boolean) => {
    // Le doigt qui reste après un pincement s'ancre sur le cadrage : borné, comme avant que le
    // rendu des gestes soit différé (cf. renderPending). Seulement pendant un pincement : à la fin
    // d'un glissement, endGesture() dessine de toute façon, et solder d'abord le rendu demandé
    // ferait une image directe de plus, aussitôt recouverte.
    if (pinch) renderPending();
    const wasActive = active.delete(e.pointerId);
    clearTimeout(holdTimer);
    if (pinch && !pinchPair()) {
      pinch = null;
      // Re-anchor on the finger that REMAINS, not the one leaving: measuring its
      // movements from the other one's position would jump the image by the whole
      // spread between the two fingers, losing the zone we just aimed at.
      const remaining = [...active.values()][0];
      if (remaining) {
        anchor(remaining);
        ptr.moved = true; // coming out of a pinch: this isn't a tap
      }
    }
    if (active.size > 0) return;
    touching = false;
    // Un jalon atteint pendant le balayage attendait ce relâchement — voir pendingHarvest. Il
    // prend la main sur le reste : la feuille vient de rendre un symbole entier, et le tap qui
    // finit un balayage ne demandait rien de plus.
    const held = pendingHarvest;
    pendingHarvest = null;
    // Lu et effacé ici pour la même raison que le jalon au-dessus : quel que soit le chemin de
    // sortie, ce doigt-ci a fini d'appuyer sur la boussole.
    const fromGuide = onGuide;
    onGuide = null;
    // Lu avant endGesture(), qui l'efface : le pan doit se terminer proprement (les calques sont
    // rasterisés au viewport final) ET ne pas retomber dans la suite, jalon ou pas.
    const wasGesture = S.gesture;
    if (wasGesture) endGesture();
    if (held) {
      held();
      return;
    }
    if (wasGesture) return;
    /**
     * Le doigt était parti de la boussole. La dérive ne l'annule plus — seul le fait d'être
     * SORTI de la cible le fait, comme sur n'importe quel bouton, et `ptr.moved` ne le regarde
     * donc pas. On relit `guideHit()` plutôt que de rejouer la cible du poser : rien n'a bougé
     * sous ce geste-ci, mais un vol encore en cours a pu déplacer le disque, et c'est le disque
     * qu'on suit.
     *
     * Sorti de la cible, il ne se passe RIEN, et surtout pas le tap ordinaire : celui-ci
     * peindrait la case sous la boussole, que personne n'a visée.
     */
    if (fromGuide) {
      if (!wasActive || !tap) return;
      const g = local(e);
      const still = guideHit(g.x, g.y);
      if (still) {
        flyToGuide(still[0], still[1]);
        buzz('light');
      }
      return;
    }
    if (!wasActive || !tap || ptr.moved) return;
    // Un tap pendant le rejeu l'abrège au lieu d'agir sur la feuille : six secondes, c'est
    // long pour qui l'a déjà vu — et c'est le seul geste qui ait un sens à ce moment-là.
    if (anim.replay) {
      animReplaySkip();
      buzz('light');
      return;
    }
    const p = local(e);
    // La boussole d'abord : elle est posée SUR la feuille, donc un tap dessus ne doit pas
    // peindre la case qui passe dessous — ni cadrer la zone qu'elle recouvre.
    const aimed = guideHit(p.x, p.y);
    if (aimed) {
      flyToGuide(aimed[0], aimed[1]);
      buzz('light');
      return;
    }
    const rc = cellAtLocal(p.x, p.y);
    if (!rc) return;
    // From the overview a tap frames the zone instead of painting: at that scale a cell is a
    // few pixels across and its code is hidden, so filling one was aiming blind.
    if (flyToZone(rc[0], rc[1]) !== null) {
      buzz('light');
      return;
    }
    // The cell the finger meant, which is the one it landed on unless it just missed — see
    // toleratedCell(). The paint-vs-pan decision above it is left strict on purpose: were it
    // tolerant too, the ground where a pan can START would shrink by one cell around every
    // remaining cell of the symbol, and in a dense region there would be nowhere left to grab
    // the sheet. The tap forgives; choosing the gesture does not.
    const t = toleratedCell(p.x, p.y) ?? rc;
    paintAt(t[0], t[1]);
  };
  S.sheet.addEventListener('pointerup', (e) => release(e, true));
  S.sheet.addEventListener('pointercancel', (e) => release(e, false));

  S.sheet.addEventListener(
    'wheel',
    (e) => {
      e.preventDefault();
      if (sheetLocked) return;
      const p = local(e);
      zoomTo(S.viewport.z * (e.deltaY < 0 ? 1.15 : 1 / 1.15), p.x, p.y);
    },
    { passive: false },
  );
}

function zoomTo(nz: number, mx: number | null, my: number | null): void {
  applyZoom(nz, mx, my);
}

// ---------- palette ----------
// Symbols still in play; null means show all of them. A motif whose every cell is filled is
// dropped: it can no longer be painted, and the row already scrolls, so keeping it only pushes
// the ones still in play out of reach.
//
// The row is read in the alphabet's order (codeOrder), not by number: the most covering symbol
// sits at the end, so nothing on the palette invites you to give the drawing away on the first
// sweep. The chips keep their code printed on them, which is what a player matches a cell
// against — the digits simply aren't in order any more in two of the five alphabets.
let shownCodes: ReadonlySet<number> | null = null;
let paletteSig = '';
/** Progress tracks, by code, so they can be moved without rebuilding the row. */
const paletteBars = new Map<number, HTMLElement>();

/**
 * Symbols with no cell left, held in the row for one beat before it closes on them. A chip that
 * vanishes the instant the last cell is filled takes the reward with it — the track reaches the
 * end and the chip is already gone. Not clickable while it closes (`.item.done` in the CSS):
 * locking a finished symbol lights no halo and turns every tap into a mistake.
 */
const closing = new Set<number>();
/** 900 ms et non plus 460 (2026-10-31) : la pastille porte maintenant une coche et sa part de la
 *  fête (cf. @keyframes chipDone), et à 460 elle était partie avant qu'on ait vu la coche. */
const CLOSE_MS = 900;

/**
 * Fed by updateProgress() on every change. The row is rebuilt only when the set of visible
 * symbols changes — buildLegend rasterizes one canvas per symbol, far too much for every
 * painted zone — but the tracks are moved on every call, which is the whole point of them.
 */
let lastProgress: readonly CodeCount[] | null = null;

/** Pushes the tally into the tracks. Called after every rebuild too, because buildLegend
 *  runs on a plain symbol click, which doesn't go through updateProgress: without this the
 *  bars would drop to zero on selecting a motif and stay there. */
function applyBars(): void {
  for (const [l, bar] of paletteBars) {
    const c = lastProgress?.[l];
    // An invisible track rather than none for a symbol absent from the target (preview mode
    // shows the whole palette): dropping it would make that swatch shorter than the others
    // and leave the row ragged.
    bar.classList.toggle('on', !!c && c.todo > 0);
    const fill = bar.firstElementChild as HTMLElement;
    fill.style.width = c && c.todo ? `${(c.done / c.todo) * 100}%` : '0%';
  }
}

export function onCodeProgress(p: readonly CodeCount[] | null): void {
  // A symbol is shown while it still has a cell to fill, in the palette's order. One absent from
  // the target (todo 0) is dropped too: an always-empty track would read as "untouched" instead
  // of "not here".
  const shown = p ? codeOrder().filter((l) => p[l].todo > 0 && p[l].done < p[l].todo) : null;
  // Symbols this very fill emptied. Only ever more than one in theory — a tap fills cells of a
  // single symbol — but the loop below costs nothing and doesn't have to assume it.
  const before = lastProgress;
  const justDone =
    p && before && filling && shown?.length
      ? codeOrder().filter(
          (l) => p[l].todo > 0 && p[l].done === p[l].todo && before[l].done < p[l].todo,
        )
      : [];
  lastProgress = p;
  const codes = shown ? new Set(shown) : null;
  // Staying locked on a symbol that just vanished would turn every tap into a mistake. We move
  // on to the next symbol left rather than back to no selection: the sheet opens locked (see
  // resetPlay), and finishing a symbol is the one moment the player is certain to want the next.
  if (codes && shown && S.lockedSymbol !== null && !codes.has(S.lockedSymbol))
    S.lockedSymbol = shown[0] ?? null;
  for (const l of justDone) {
    symbolDone(l, filling![0], filling![1]);
    // The chip's exit beat. Skipped when motion is off: held without its animation it would just
    // be a chip that lingers, which reads as the row failing to update.
    if (noMotion()) continue;
    closing.add(l);
    setTimeout(() => {
      if (closing.delete(l)) buildLegend();
    }, CLOSE_MS);
  }
  // The selection is part of the signature: the palette draws the live chip, so a selection that
  // changes without the chips changing — a new sheet opening on its first symbol, the line above
  // moving on — still has to redraw the row.
  const sig = `${shown ? shown.join(',') : 'all'}|${S.lockedSymbol}`;
  if (sig !== paletteSig) {
    paletteSig = sig;
    shownCodes = codes;
    buildLegend(); // already applies the bars
    return;
  }
  applyBars();
}

export function buildLegend(): void {
  const wrap = $('legend');
  // The chips go in a track inside the box: the box is what scrolls when they no longer fit
  // in two rows, the track is what wraps. See balanceLegend().
  const track = document.createElement('div');
  track.className = 'track';
  // The palette wraps, so rebuilding it can change its height: an alphabet with narrow
  // swatches fits on one line, a finished symbol takes its chip out of the row. That height
  // is the sheet's — measured before, compared after, and only then do we pay for a
  // re-measure of the scene.
  const h0 = wrap.offsetHeight;
  wrap.innerHTML = '';
  paletteBars.clear(); // the elements they pointed at have just been thrown away
  const alpha = alphabet();
  // Swatch height. It sets the height of the whole palette row, which is taken from the
  // sheet: going much beyond this starts costing visible rows of the grid.
  const ch = 36;
  const ar = alpha.aspect();
  // a swatch in the glyph's aspect ratio: square for shapes, narrow for monospace
  const cw = ar < 1 ? Math.max(20, Math.round(ch * ar) + 6) : ch;
  // Avant le premier décompte (shownCodes encore nul), la rangée montrait toute la palette. Pour
  // un nuancier de 256 couleurs dont la feuille n'en emploie qu'une vingtaine (peintureNuancee),
  // c'était 256 pastilles rastérisées pour rien, et une rangée qui ne ressemble à aucune partie :
  // au-delà de l'ancien plafond de 40 codes, on ne montre que les couleurs de CETTE feuille.
  const order = codeOrder(alpha);
  let present: Set<number> | null = null;
  if (!shownCodes && order.length >= 40) present = new Set(S.target.flat());
  for (const l of order) {
    if (present && !present.has(l)) continue;
    // A chip is drawn while its symbol is in play, plus the beat a symbol just finished gets.
    const done = !!shownCodes && !shownCodes.has(l);
    if (shownCodes && !shownCodes.has(l) && !closing.has(l)) continue;
    const item = document.createElement('div');
    // Joué dans l'ordre (orderedPlay) : les pastilles à venir s'affichent, pour qu'on voie ce
    // qui reste, mais en attente — elles ne répondent pas (cf. onclick).
    const later = !!alpha.orderedPlay && !done && l !== S.lockedSymbol;
    item.className = `item${l === S.lockedSymbol ? ' sel' : ''}${done ? ' done' : ''}${later ? ' later' : ''}`;
    if (alpha.codeName) item.title = alpha.codeName(l);
    const c = document.createElement('canvas');
    c.width = cw * S.DPR;
    c.height = ch * S.DPR;
    c.style.width = `${cw}px`;
    c.style.height = `${ch}px`;
    const cx = c.getContext('2d')!;
    cx.scale(S.DPR, S.DPR);
    if (alpha.swatch) alpha.swatch(cx, cw, ch, l);
    else alpha.draw(cx, 0, 0, cw, ch, l);
    item.appendChild(c);
    // Track pinned to the swatch width, not the chip's: aligned on the glyph's edges it
    // reads as a rule measuring that motif, instead of a badge stuck onto the chip.
    const bar = document.createElement('div');
    bar.className = 'fill';
    bar.style.width = `${cw}px`;
    bar.appendChild(document.createElement('i'));
    item.appendChild(bar);
    paletteBars.set(l, bar);
    // Le numéro de la pastille renvoie à celui des cases : sans numéros sur la feuille
    // (bareSheet), il ne désignerait plus rien.
    if (!alpha.bareSheet) {
      const n = document.createElement('span');
      n.className = 'n';
      n.textContent = String(l);
      item.appendChild(n);
    }
    item.onclick = () => {
      // A chip selects, it never deselects. Tapping the live one used to unlock, which handed
      // the player the one state the game has no use for: no symbol locked means a tap fills
      // whatever it lands on and every zone counter tallies the whole sheet. It was reached by
      // accident — a second tap on the chip you had just chosen — and the way out of it was not
      // obvious. Switching symbol is still one tap, on another chip.
      //
      // Joué dans l'ordre, aucune autre pastille ne se prend : la suivante viendra d'elle-même
      // quand celle-ci sera finie. Même réponse qu'au tap sur la pastille en cours — le geste a
      // été vu, il n'y a rien à changer.
      if (S.lockedSymbol === l || alpha.orderedPlay) {
        buzz('light'); // the tap was seen; there is simply nothing to change
        // … et une pastille à venir dit pourquoi elle ne se prend pas (cf. ordre.ts).
        if (S.lockedSymbol !== l) ordrePastilleAVenir();
        return;
      }
      S.lockedSymbol = l;
      S.hover = null;
      S.hoverKey = '';
      buildLegend();
      invalidateHalo();
      {
        // The counts follow the lock, so picking a symbol re-tallies every zone — and the answer
        // to "where is this one left to do" is the whole reason to pick it. Without the poke that
        // answer arrives at the rest opacity whenever the sheet had been left alone for a couple
        // of seconds, which is exactly the pause somebody browses the palette in. Same treatment
        // as a finger on the sheet: the numbers come up full and start their spell over.
        animPoke();
        render();
        animWake();
      }
      buzz('light');
    };
    track.appendChild(item);
  }
  wrap.appendChild(track);
  syncBottomBar();
  applyBars();
  balanceLegend(wrap, track);
  // Une seule fois, sur la boîte et non sur la piste : la piste est jetée à chaque
  // reconstruction, la boîte survit à la partie entière.
  if (!wrap.dataset['faded']) {
    wrap.dataset['faded'] = '1';
    wrap.addEventListener('scroll', () => fadeLegend(wrap), { passive: true });
  }
  fadeLegend(wrap);
  if (wrap.offsetHeight !== h0) afterLayout();
}

/** Brings the locked chip back into view when the palette scrolls. Not a nicety: a symbol
 *  running out moves the lock on its own (see updateProgress), and the chip it moves to can
 *  be off-screen — the player would be left looking at a palette whose selected chip is
 *  nowhere on it. */
/**
 * Les deux fondus de bord, posés d'après la position RÉELLE du défilement : à gauche seulement
 * s'il y a quelque chose derrière, à droite seulement s'il en reste devant.
 *
 * Le seuil de 2 px n'est pas de la superstition : `scrollWidth` et `clientWidth` sont arrondis
 * au pixel entier alors que la mise en page est en sous-pixel, donc une palette qui tient
 * exactement s'annonce couramment comme dépassant de un pixel. Fader 14 px pour un pixel caché,
 * c'était précisément le défaut qu'on corrige.
 */
function fadeLegend(wrap: HTMLElement): void {
  const reste = wrap.scrollWidth - wrap.clientWidth - wrap.scrollLeft;
  wrap.classList.toggle('fade-l', wrap.scrollLeft > 2);
  wrap.classList.toggle('fade-r', reste > 2);
}

function revealLocked(wrap: HTMLElement): void {
  const sel = wrap.querySelector<HTMLElement>('.item.sel');
  if (!sel || wrap.scrollWidth <= wrap.clientWidth) return;
  const left = sel.offsetLeft;
  const right = left + sel.offsetWidth;
  if (left >= wrap.scrollLeft && right <= wrap.scrollLeft + wrap.clientWidth) return;
  wrap.scrollTo({
    left: left - (wrap.clientWidth - sel.offsetWidth) / 2,
    behavior: noMotion() ? 'auto' : 'smooth',
  });
}

/**
 * Evens out the wrapped rows, and caps the palette at two of them.
 *
 * Left to itself, flex fills the first line and drops the remainder on the next: nine chips
 * come out as 7 + 2, which reads as a row that overflowed rather than as the whole palette.
 * Narrowing the track to the per-row count gives 5 + 4.
 *
 * Past two rows the palette is no longer worth the sheet it costs — the paper is fitted to the
 * width, so the first two rows are paid for out of dead space above and below it, the third
 * one is paid for in visible cells. The seventeen chips of the line-shapes alphabet ask for
 * three. So beyond two rows the track keeps its full width, overflows the box, and the box
 * scrolls: a chip pushed off the side is one swipe away instead of costing everyone a row of
 * the drawing. What it gives up is the guarantee stated in the palette's CSS — a symbol you
 * cannot see is a symbol you cannot lock — hence revealLocked() above, and the fade at both
 * ends that says there is more.
 *
 * Measured rather than computed: the chip's width is the swatch plus a padding the media
 * queries change, so only the laid-out element knows it.
 */
const MAX_ROWS = 2;

function balanceLegend(wrap: HTMLElement, track: HTMLElement): void {
  track.style.width = '';
  wrap.classList.remove('scroll');
  const first = track.firstElementChild as HTMLElement | null;
  if (!first) return;
  const n = track.children.length;
  const cs = getComputedStyle(wrap);
  const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
  // box-sizing is border-box here, so the padding is inside both the measure and the cap.
  const pad = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
  const step = first.offsetWidth + gap;
  const fit = Math.max(1, Math.floor((wrap.clientWidth - pad + gap) / step));
  if (fit >= n) return; // one line, nothing to balance
  const rows = Math.min(MAX_ROWS, Math.ceil(n / fit));
  const perRow = Math.ceil(n / rows);
  track.style.width = `${perRow * step - gap}px`;
  if (perRow > fit) {
    wrap.classList.add('scroll');
    revealLocked(wrap);
  }
}

/**
 * Ouvre la feuille en partie. Il n'y a plus qu'un mode : le mode Aperçu servait à régler une
 * conversion, et la conversion appartient maintenant à l'admin — le jeu reçoit des grilles
 * faites, il les joue.
 */
export function startPlay(): void {
  coachNouvelleFeuille();
  ordreNouvelleFeuille();
  $('finished').classList.remove('on');
  // Avant resetPlay(), qui remet S.gameOver à faux : on veut l'état de la feuille qu'on ouvre,
  // pas celui qu'on quitte. syncAgain() est rappelé par replaySequence() juste après pour une
  // feuille déjà terminée.
  syncAgain();
  invalidateLayers();
  resetPlay();
  afterLayout();
  syncAgain();
}

/**
 * To be called whenever the space offered to the sheet changes (help expanded, panel
 * opened, rotation, switching to Play mode which makes the gauge appear). CSS
 * transitions take time, so we also re-measure once they're done.
 */
export function afterLayout(): void {
  const recheck = () => {
    const w = S.availW;
    const h = S.availH;
    measureScene();
    if (S.availW === w && S.availH === h) return;
    invalidateLayers();
    // The measurement is always worth taking; the drawing only exists once a grid does.
    // buildLegend() gets here from start(), before the demo image has been converted, and
    // render() on an empty S.target threw — taking the rest of the startup with it.
    //
    // Même piège en changeant de feuille : applyPuzzle() (home.ts) et la feuille d'essai
    // appellent buildLegend() AVANT resetPlay(), donc avec le `target` de la nouvelle grille et
    // le `filled` de l'ancienne. Tant que la palette gardait sa hauteur, on ne passait pas ici.
    // Une palette sans numéros (bareSheet) est plus basse : la hauteur change, render() part, et
    // buildZones lisait une ligne de `filled` qui n'existe pas (2026-10-13). resetPlay() dessine
    // de toute façon juste après, et startPlay() remesure : sauter ce rendu-ci ne perd rien.
    const coherent = S.filled.length === S.gridH && S.filled[0]?.length === S.gridW;
    if (S.target.length && coherent) render();
  };
  recheck();
  window.setTimeout(recheck, 360);
}

/** Returns true if a panel was open: Android's back button then closes it instead of
 *  quitting the app. */
export function closeOverlays(): boolean {
  // First, because it is the topmost thing on screen: back must close what is in front.
  // La feuille du pseudo s'ouvre par-dessus la page des réglages, donc elle est vérifiée avant
  // tout le reste. Réglages, elle, n'est plus un dialogue à refermer mais un onglet : son retour
  // est un maillon plus loin dans la chaîne de main.ts (backFromPrefs, home.ts).
  // Le dialogue des deux gestes d'abord : « Revoir les gestes » l'ouvre par-dessus les réglages,
  // et à la première feuille il couvre tout le reste.
  if (closeGestureDialog()) return true;
  // La fin de la pièce d'initiation : « précédent » y vaut « terminer ce dessin » (mascot.ts).
  if (closePieceDone()) return true;
  if (closeUsernameDialog()) return true;
  // Même rang : la feuille de la langue s'ouvre elle aussi par-dessus la page des réglages.
  if (closeLangSheet()) return true;
  // La feuille de partage, ouverte par-dessus la feuille terminée.
  if (closeShareSheet()) return true;
  // Et le code de reprise, troisième feuille du même rang.
  if (closeRecoveryDialog()) return true;
  for (const id of ['finished']) {
    if ($(id).classList.contains('on')) {
      $(id).classList.remove('on');
      return true;
    }
  }
  return false;
}

// ---------- wiring ----------
export function wireUI(): void {
  wirePointer();
  wireShareSheet();
  $('finished').onclick = () => $('finished').classList.remove('on');
  // L'adresse publique du jeu, passée au build depuis la source unique (cf. prod-urls.sh, qui la
  // lit dans environment.prod.ts). Sans elle on ne peut pas fabriquer de lien partageable : dans
  // l'APK, `location.origin` vaut https://localhost et ne mène nulle part.
  const GAME_URL = (import.meta.env.VITE_GAME_URL as string | undefined) ?? '';
  // `/p` et non `/p.html` : Cloudflare Pages normalise l'extension et répond 308 vers la forme
  // sans elle. Émettre la forme canonique évite une redirection sur un lien qu'on partage, et
  // c'est aussi celle qu'on lit dans un message. Vérifié en ligne : les deux répondent, la
  // première directement.
  const PAGE = '/p';
  $('shareBtn').onclick = () => {
    if (!S.puzzleId) return;
    // Le bouton se montre pendant le rejeu (cf. syncAgain) : on le termine, comme un tap sur la
    // feuille, pour que l'image — faite sur S.filled — parte avec le dessin entier et pas avec
    // ce que le rejeu en avait déjà révélé. Le choix de l'image ou de la vidéo laisse une image
    // au rejeu pour finir avant que la préparation lise la feuille.
    animReplaySkip();
    const mine = isCreation(S.puzzleId);
    // Retenu ici : le sélecteur rend la main bien plus tard, et le joueur peut avoir ouvert une
    // autre feuille entre-temps.
    const sharedId = S.puzzleId;
    // `lang` sert à la CARTE D'APERÇU du lien, pas à la page : celle-ci choisit déjà la langue
    // de qui l'ouvre (cf. pickLang()). Les robots des messageries n'exécutent aucun script, donc
    // functions/p.js n'a que l'URL pour savoir dans quelle langue écrire la carte — sans ce
    // paramètre elle repart en anglais, ce qui reste lisible mais perd la langue de l'expéditeur.
    const url = GAME_URL
      ? `${GAME_URL.replace(/\/$/, '')}${PAGE}?id=${encodeURIComponent(S.puzzleId)}&ink=${encodeURIComponent(settings().ink)}&lang=${currentLang()}`
      : '';
    // L'IMAGE OU LA VIDÉO, au choix, partout où on sait partager un fichier (cf. src/sharePanel.ts,
    // qui écrit le message et y met le lien de la fiche Play Store).
    if (canShareFile()) {
      openShareSheet({
        mine,
        countAs: mine ? null : sharedId,
        creationId: mine ? sharedId : null,
      });
      return;
    }
    // Sinon le LIEN, comme avant la vidéo. Plus aucune plateforme n'y arrive depuis que
    // l'application partage des fichiers (2026-10-18), mais c'est le repli si canShareFile() en
    // exclut une un jour. Jamais pour une création, que le bouton ne montre d'ailleurs pas là.
    if (mine || !url) return;
    // L'accroche est celle de la fiche du Store, mot pour mot : c'est déjà la phrase la mieux
    // écrite du projet, et deux formulations du même argument finiraient par se contredire.
    void shareSheet({
      title: t('brand.name'),
      text: t('game.shareText'),
      url,
    }).then((r) => {
      // Le badge « Partage » : une feuille partagée, ou dont le lien est parti au presse-papiers.
      // On ne saura jamais si le message a vraiment été envoyé — aucune API ne le dit — d'où la
      // règle côté serveur, une feuille comptée une fois (cf. src/shares.ts).
      if (r !== 'échec' && sharedId) noteShare(sharedId);
      // Le retour se fait sur le bouton lui-même plutôt que par une bulle : le sélecteur du
      // système est déjà sa propre confirmation, et il n'y a qu'un seul cas à annoncer — celui
      // où il n'y a pas de sélecteur et où le lien est parti au presse-papiers sans rien montrer.
      if (r !== 'copié') return;
      const b = $('shareBtn');
      const avant = b.textContent;
      b.textContent = t('game.linkCopied');
      window.setTimeout(() => {
        b.textContent = avant;
      }, 1800);
    });
    buzz('light');
  };
  $('againBtn').onclick = () => {
    if (!S.gameOver || anim.replay || anim.final) return; // déjà en cours : rien à faire
    $('finished').classList.remove('on'); // la carte gêne ce qu'on vient demander à revoir
    replaySequence();
    buzz('light');
  };
  // Une étoile déjà active qu'on reclique repose la note à 0 : « entre 0 et 5 » veut dire que 0
  // est un choix qu'on peut reprendre, pas seulement l'absence de note au tout premier passage.
  for (const btn of $('stars').querySelectorAll<HTMLButtonElement>('.star')) {
    const v = Number(btn.dataset.v);
    btn.onclick = () => {
      if (!S.puzzleId) return; // le puzzle de démo n'est jamais sauvegardé
      S.rating = S.rating === v ? 0 : v;
      rateProgress(S.puzzleId, S.rating);
      // Le ciblage des demandes d'avis (review.ts) lit ces mêmes étoiles : une note posée dit
      // gratuitement si le joueur est content. Le zéro du re-clic n'est pas une note, le
      // module l'ignore de lui-même.
      markSheetRated(S.rating);
      syncAgain();
      buzz('light');
    };
  }
  // Changer d'encre repeint tout ce qui en est fait : les couches en cache (la feuille), et les
  // pastilles de la palette, qui sont des canevas dessinés avec la même encre. Comparé plutôt
  // que repeint à chaque réglage : basculer le son ne coûte pas une reconstruction de la feuille.
  //
  // Le repère des cases à faire suit la même règle : sa forme et sa teinte sont cuites dans la
  // couche en cache du halo, donc en changer demande de la rebâtir — mais pas les pastilles de la
  // palette, qui n'en portent rien.
  let painted = settings().ink;
  let marque = `${settings().markStyle}/${settings().markHue}`;
  let boussole = settings().compass;
  wireSettings(() => {
    const m = `${settings().markStyle}/${settings().markHue}`;
    const encre = settings().ink !== painted;
    const repere = m !== marque;
    const bous = settings().compass !== boussole;
    if (!encre && !repere && !bous) return;
    painted = settings().ink;
    marque = m;
    boussole = settings().compass;
    // La boussole se dessine SUR LA VITRE et non dans une couche en cache (cf. drawGuide) :
    // l'allumer ou l'éteindre ne demande qu'une image. L'encre et le repère, eux, y sont cuits,
    // donc eux seuls valent une reconstruction — la bascule de la boussole ne la paie plus.
    if (encre || repere) invalidateLayers();
    if (encre) buildLegend();
    render();
  });

  // resize / rotation
  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      const w = S.availW;
      const h = S.availH;
      measureScene();
      if (S.availW !== w || S.availH !== h) {
        S.viewport = { z: 1, x: 0, y: 0 };
        invalidateLayers();
        render();
      }
    }, 140);
  });
}
